import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { getJson } from "../utils/earlyData";
import ArticleCard from "../components/ArticleCard";
import PersonCard from "../components/PersonCard";
import VideoCard from "../components/VideoCard";
import Newsletter from "../components/Newsletter";
import HeroCarousel from "../components/HeroCarousel";
import SubmitStoryCTA from "../components/SubmitStoryCTA";
import BannerSlot from "../components/BannerSlot";
import SEO, { ORGANIZATION, ORG_ID, WEBSITE_ID } from "../components/SEO";
import TopStories from "../components/TopStories";

// PWM Pulse (external feed) sits far below the fold; its own chunk keeps it out of the critical bundle.
const GlobalNewsSection = lazy(() => import("../components/GlobalNewsSection"));

const SectionHeader = ({ overline, title, italicWord, viewAllHref, viewAllLabel = "View All", dark = false }) => (
  <div className="flex items-end justify-between mb-12">
    <div>
      <span className={dark ? "editorial-overline-light mb-3 block" : "editorial-overline mb-3 block"}>{overline}</span>
      <h2 className={`font-serif text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight ${dark ? "text-white" : "text-neutral-950"}`}>
        {italicWord ? (
          <>{title.split(italicWord)[0]}<em className="italic font-normal">{italicWord}</em>{title.split(italicWord)[1]}</>
        ) : title}
      </h2>
    </div>
    {viewAllHref && (
      <Link
        to={viewAllHref}
        className={`hidden md:flex items-center gap-1.5 text-sm font-medium transition-colors ${dark ? "text-neutral-500 hover:text-white" : "text-neutral-500 hover:text-neutral-950"}`}
        data-testid={`view-all-${overline.toLowerCase().replace(/\s/g, '-')}`}
      >
        {viewAllLabel}
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    )}
  </div>
);

// Grids look ragged with 4-5 cards: show 6 when available, else 3, else whatever exists.
const cleanGrid = (list) => list.slice(0, list.length >= 6 ? 6 : list.length >= 3 ? 3 : list.length);

const SITE = "https://peoplewhomatter.in";
const SITE_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    ORGANIZATION,
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: `${SITE}/`,
      name: "People Who Matter",
      description: "People Who Matter is a new age, technology enabled digital editorial platform focused on the people, brands, companies and initiatives shaping business, culture and contemporary India.",
      publisher: { "@id": ORG_ID },
      inLanguage: "en-IN",
      potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${SITE}/search?q={search_term_string}` }, "query-input": "required name=search_term_string" },
    },
  ],
};

// Uniform vertical rhythm for every homepage module; backgrounds strictly alternate white / light grey in render order.
const TONE = ["bg-white", "bg-[#F5F5F4] tone-grey"];
const SECTION_PAD = "py-12 md:py-16";

const CategorySection = ({ id, tone, overline, title, italicWord, href, viewAllLabel, articles }) => (
  <section className={`${SECTION_PAD} ${tone}`} data-testid={id}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader overline={overline} title={title} italicWord={italicWord} viewAllHref={href} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
        {cleanGrid(articles).map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>
      <div className="mt-8 md:hidden">
        <Link to={href} className="flex items-center justify-center gap-2 py-3 text-neutral-950 text-sm font-semibold tracking-wider uppercase hover:bg-neutral-50 transition-colors" style={{ border: '1px solid rgba(10,10,10,0.1)' }}>
          {viewAllLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getJson(`/articles?limit=200&compact=true`)
      .then((data) => setArticles(data || []))
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
    axios.get(`${API}/articles/trending?limit=4`).then((r) => setTrending(r.data || [])).catch(() => setTrending([]));
  }, []);

  // Everything is chronological: the API returns published articles newest first.
  const byCat = (c) => articles.filter((a) => a.category === c);
  const leadStory = articles[0];
  const secondaryStories = articles.slice(1, 5);
  const specialsArticles = byCat("specials");
  const peopleArticles = byCat("people");
  const companyArticles = byCat("companies");
  const brandArticles = byCat("brands");
  const campaignArticles = byCat("initiatives");
  const videoArticles = articles.filter((a) => a.video_url);
  const moreUpdates = articles.slice(0, 5);
  const trendingList = (trending.length ? trending : articles.filter((a) => a.is_featured)).slice(0, 4);

  // Render order → alternating tone. Ad banners are thin strips between modules and do not consume a tone.
  const blocks = [
    leadStory && { id: "top-stories", render: (tone) => <TopStories lead={leadStory} secondary={secondaryStories} tone={tone} pad={SECTION_PAD} /> },
    { id: "banner-top", strip: true, render: () => <BannerSlot slot="home-top" /> },
    specialsArticles.length > 0 && { id: "specials", render: (tone) => <CategorySection id="specials-section" tone={tone} overline="Editor's Picks" title="PWM Specials" italicWord="PWM" href="/category/specials" viewAllLabel="View All Specials" articles={specialsArticles} /> },
    peopleArticles.length > 0 && { id: "people", render: (tone) => <CategorySection id="people-section" tone={tone} overline="Featured Profiles" title="People Who Matter" italicWord="People" href="/category/people" viewAllLabel="View All People" articles={peopleArticles} /> },
    companyArticles.length > 0 && { id: "companies", render: (tone) => <CategorySection id="companies-section" tone={tone} overline="Business &amp; Impact" title="Companies That Matter" italicWord="Companies" href="/category/companies" viewAllLabel="View All Companies" articles={companyArticles} /> },
    { id: "banner-mid", strip: true, render: () => <BannerSlot slot="home-mid" /> },
    brandArticles.length > 0 && { id: "brands", render: (tone) => <CategorySection id="brands-section" tone={tone} overline="Brand Stories" title="Brands That Matter" italicWord="Brands" href="/category/brands" viewAllLabel="View All Brands" articles={brandArticles} /> },
    campaignArticles.length > 0 && { id: "initiatives", render: (tone) => <CategorySection id="initiatives-section" tone={tone} overline="Purpose in Action" title="Initiatives That Matter" italicWord="Initiatives" href="/category/initiatives" viewAllLabel="View All Initiatives" articles={campaignArticles} /> },
    videoArticles.length > 0 && { id: "videos", dark: true, render: () => (
      <section className={`${SECTION_PAD} bg-[#050505] noise-overlay`} data-testid="videos-section">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader overline="Video Interviews" title="Videos That Matter" italicWord="Videos" dark />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videoArticles.slice(0, 3).map((article) => <VideoCard key={article.id} article={article} />)}
          </div>
        </div>
      </section>
    ) },
    { id: "pulse", render: (tone) => <Suspense fallback={<section className={`${SECTION_PAD} ${tone}`} aria-hidden="true" />}><GlobalNewsSection tone={tone} pad={SECTION_PAD} /></Suspense> },
    { id: "submit", render: (tone) => <SubmitStoryCTA variant="home" tone={tone} /> },
    { id: "news", render: (tone) => (
      <section className={`${SECTION_PAD} ${tone}`} data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-8">
              <SectionHeader overline="Stay Informed" title="More Updates" italicWord="More" viewAllHref="/category/latest" viewAllLabel="All stories" />
              <div>
                {moreUpdates.map((article) => <ArticleCard key={article.id} article={article} variant="horizontal" />)}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="sticky-sidebar space-y-12">
                <div>
                  <h3 className="editorial-overline mb-6">Trending Now</h3>
                  <div className="space-y-0">
                    {trendingList.map((article, index) => (
                      <Link key={article.id} to={`/article/${article.slug}`} className="flex gap-5 group py-5" style={{ borderBottom: '1px solid rgba(10,10,10,0.06)' }} data-testid={`trending-article-${index}`}>
                        <span aria-hidden="true" data-n={String(index + 1).padStart(2, '0')} className="font-serif text-3xl font-black text-neutral-200 group-hover:text-wine-700 transition-colors leading-none before:content-[attr(data-n)]" />
                        <div>
                          <h4 className="font-serif text-sm font-semibold text-neutral-950 group-hover:text-wine-700 transition-colors line-clamp-2 leading-snug">{article.title}</h4>
                          <span className="text-neutral-500 text-xs mt-1 block">{article.author}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="p-6" style={{ border: '1px solid rgba(10,10,10,0.06)' }}>
                  <h3 className="editorial-overline mb-6">Explore</h3>
                  <div className="space-y-0">
                    {[
                      { label: "People", slug: "people" },
                      { label: "Companies", slug: "companies" },
                      { label: "Brands", slug: "brands" },
                      { label: "Initiatives", slug: "initiatives" },
                      { label: "Specials", slug: "specials" },
                    ].map((category) => (
                      <Link key={category.slug} to={`/category/${category.slug}`} className="flex items-center justify-between py-3 text-neutral-500 hover:text-neutral-950 transition-colors group text-sm" style={{ borderBottom: '1px solid rgba(10,10,10,0.04)' }}>
                        <span>{category.slug === "specials" ? "PWM Specials" : `${category.label} That Matter`}</span>
                        <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    ) },
  ].filter(Boolean);

  let toneIndex = 0;
  // The hero (LCP) renders immediately; modules below fill in when the article list arrives.
  // The placeholder keeps the page height stable so nothing visible shifts when content lands.
  const rendered = loading ? [<div key="loading" className="min-h-[70vh] bg-white" aria-hidden="true" />] : blocks.map((b) => {
    if (b.strip || b.dark) return <div key={b.id}>{b.render()}</div>;
    const tone = TONE[toneIndex % 2];
    toneIndex += 1;
    return <div key={b.id}>{b.render(tone)}</div>;
  });

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title={null}
        description="Stories on the people, brands, companies and initiatives shaping business, culture and contemporary India. We find what matters, and tell you why."
        url="/"
        jsonLd={SITE_JSON_LD}
      />
      <HeroCarousel articles={articles} />
      {rendered}
      <Newsletter />
    </div>
  );
};

export default HomePage;
